import Image from '@atoms/Image';
import { PortableText as SanityPortableText } from '@portabletext/react';
import Gallery from '@sanity/components/Gallery';
import GoogleMapsEmbed from '@sanity/components/GoogleMapsEmbed';
import ImageWrapper from '@sanity/components/ImageWrapper';
import Video from '@sanity/components/Video';
import Link from 'next/link';
import type { PropsWithChildren, ReactElement } from 'react';
import { useState } from 'react';
import { InView } from 'react-intersection-observer';

import { changeStringToId, isLocalDomain } from '@/utils';

import { Description, PortableTextWrapper } from './PortableText.styled';
import type { PortableTextProps } from './PortableText.types';
import type { LinkMarkProps } from './PortableText.types';

const LinkMark = ({
  value,
  children,
}: PropsWithChildren<LinkMarkProps>): ReactElement => {
  if (!value || !value.href) return;
  if (isLocalDomain(value.href)) {
    return <Link href={value.href}>{children}</Link>;
  }

  return (
    <a href={value.href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

export const PortableText = ({
  value,
  additionalComponents,
}: PortableTextProps): ReactElement => {
  const [mapInView, setMapInView] = useState<boolean>(false);
  if (!value) return null;

  const components = {
    types: {
      image: ({ value }) => {
        if (!value?.image?.asset?.url) {
          return null;
        }
        const fill = value.objectFit === 'contain';

        return (
          <ImageWrapper fill={fill}>
            <Image
              alt={value.alt}
              src={value.image.asset.url}
              blurDataURL={value.image.asset.metadata.lqip}
              rounded
              height={!fill ? 500 : undefined}
              width={!fill ? 700 : undefined}
              fill={value.objectFit === 'contain'}
              sizes="(max-width: 991px) 100vw, 700px"
              placeholder="blur"
              quality={70}
              style={{
                display: 'block',
                objectFit: fill ? 'fill' : 'cover',
                maxWidth: '100%',
                maxHeight: '600px',
                margin: '16px auto 8px',
                ...(fill ? {} : { height: 'auto' }),
                ...(fill ? {} : { width: 'auto' }),
              }}
            />
            {value.description && (
              <Description>{value.description}</Description>
            )}
          </ImageWrapper>
        );
      },
      googleMaps: ({ value }) => (
        <InView
          onChange={inView => {
            if (inView) {
              setMapInView(true);
            }
          }}
        >
          <GoogleMapsEmbed url={mapInView ? value.url : ''} />
        </InView>
      ),
      gallery: ({ value }) => (
        <Gallery images={value.images} description={value.description} />
      ),
      video: ({ value }) => {
        const { url, title } = value;
        if (!url) return null;

        return (
          <>
            <Video url={url} title={title || 'Film'} />
            {value.title && <Description>{value.title}</Description>}
          </>
        );
      },
      ...(additionalComponents?.types || {}),
    },
    block: {
      h2: ({ children }) => {
        // TODO: Figure out better way of handling text to ID conversion
        const h2Id =
          typeof children[0] === 'string'
            ? changeStringToId(children[0])
            : changeStringToId(children[0].props.children[0]);
        return <h2 id={h2Id}>{children}</h2>;
      },
      ...(additionalComponents?.block || {}),
    },
    marks: {
      link: LinkMark,
      internalLink: LinkMark,
      ...(additionalComponents?.marks || {}),
    },
  };

  return (
    <PortableTextWrapper>
      <SanityPortableText value={value} components={components as never} />
    </PortableTextWrapper>
  );
};
